import api from './api';

export interface PendingApproval {
  id: string;
  image_id: string;
  batch_id: string;
  b2_bucket: string;
  b2_file_path: string;
  thumbnail_b2_path?: string;
  original_filename?: string;
  file_size_bytes?: number;
  resolution?: string;
  quality_score?: number;
  quality_tier?: string;
  title?: string;
  description?: string;
  keywords?: string[];
  category?: string;
  generated_metadata?: any;
  recommended_platforms?: string[];
  selected_platforms?: string[];
  platform_specific_metadata?: any;
  status: string;
  reviewed_at?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  preview_url?: string;
  thumbnail_url?: string;
  full_url?: string;
  ai_analysis_at?: string;
  file_format?: string;
}

export interface ApprovalStats {
  total_pending: number;
  total_approved_today: number;
  total_rejected_today: number;
  pending_premium: number;
  pending_standard: number;
  pending_bulk: number;
  avg_quality_score: number;
}

export interface PaginatedResponse<T> {
  total: number;
  page: number;
  page_size: number;
  items: T[];
}

export interface ApprovalFilters {
  quality_tier?: string;
  batch_id?: string;
  min_quality_score?: number;
  status?: string;
}

export interface ApprovalAction {
  approval_id: string;
  platforms: string[];
  custom_metadata?: any;
}

export interface RejectionAction {
  approval_id: string;
  reason: string;
  notes?: string;
}

export interface MetadataUpdate {
  title?: string;
  description?: string;
  keywords?: string[];
  category?: string;
  selected_platforms?: string[];
}

export interface RejectedImage {
  id: string;
  image_id: string;
  original_filename: string;
  thumbnail_url?: string;
  quality_score?: number;
  rejection_reason: string;
  rejected_at: string;
  rejected_by?: string;
}

export interface ApprovedImage {
  id: string;
  image_id: string;
  original_filename: string;
  thumbnail_url?: string;
  quality_score?: number;
  platforms: string[];
  approved_at: string;
  approved_by?: string;
  upload_status: {
    [platform: string]: 'pending' | 'uploading' | 'success' | 'failed';
  };
}

export interface ApprovalResponse {
  approval_id: string;
  status: string;
  approved_at?: string;
  rejected_at?: string;
  platforms?: string[];
  rejection_reason?: string;
}

class ApprovalsService {
  async getStats(): Promise<ApprovalStats> {
    const response = await api.get<ApprovalStats>('/approvals/stats');
    return response.data;
  }

  async getPendingApprovals(
    page = 1,
    pageSize = 20,
    filters?: {
      quality_tier?: string;
      batch_id?: string;
      date_from?: string;
      date_to?: string;
    }
  ): Promise<PaginatedResponse<PendingApproval>> {
    const params = {
      page,
      page_size: pageSize,
      ...filters,
    };
    const response = await api.get<PaginatedResponse<PendingApproval>>('/approvals/pending', { params });
    return response.data;
  }

  async getApproval(id: string): Promise<PendingApproval> {
    const response = await api.get<PendingApproval>(`/approvals/${id}`);
    return response.data;
  }

  async approve(action: ApprovalAction): Promise<void> {
    await api.post(`/approvals/${action.approval_id}/approve`, {
      platforms: action.platforms,
      custom_metadata: action.custom_metadata,
    });
  }

  async reject(action: RejectionAction): Promise<any> {
    const response = await api.post(`/approvals/${action.approval_id}/reject`, {
      reason: action.reason,
      notes: action.notes,
    });
    return response.data;
  }

  async batchApprove(approvalIds: string[], platforms: string[]): Promise<void> {
    await api.post('/approvals/batch-approve', {
      approval_ids: approvalIds,
      platforms,
    });
  }

  async batchReject(approvalIds: string[], reason: string): Promise<any> {
    const response = await api.post('/approvals/batch-reject', {
      approval_ids: approvalIds,
      reason,
    });
    return response.data;
  }

  async updateMetadata(id: string, metadata: MetadataUpdate): Promise<void> {
    await api.put(`/approvals/${id}/metadata`, metadata);
  }

  async deleteApproval(id: string): Promise<void> {
    await api.delete(`/approvals/${id}`);
  }

  async getRejected(
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResponse<PendingApproval>> {
    const params = {
      page,
      page_size: pageSize,
      status: 'rejected',
    };
    const response = await api.get<PaginatedResponse<PendingApproval>>('/approvals/pending', { params });
    return response.data;
  }

  async getApproved(
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResponse<PendingApproval>> {
    const params = {
      page,
      page_size: pageSize,
      status: 'approved',
    };
    const response = await api.get<PaginatedResponse<PendingApproval>>('/approvals/pending', { params });
    return response.data;
  }

  async getRejectedImages(filters?: {
    reason?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<RejectedImage[]> {
    const params = { ...filters };
    const response = await api.get<RejectedImage[]>('/approvals/rejected-analysis', { params });
    return response.data;
  }

  async getApprovedImages(filters?: {
    platform?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<ApprovedImage[]> {
    const params = { ...filters };
    const response = await api.get<ApprovedImage[]>('/approvals/approved-analysis', { params });
    return response.data;
  }
}

const approvalsService = new ApprovalsService();
export default approvalsService;